import React, { useContext, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import classnames from 'classnames';

import LogoImg from 'assets/images/ay/ay-logo.svg';
import CompaniesPropertiesSearchInput from 'components/CompaniesPropertiesSearchInput';
import CreateHistoricalPerformance from 'components/HistoricalPerformance/HistoricalPerformanceModal/CreateHistoricalPerformance';
import { NavBarGridControl } from 'components/NavBarGridControl';
import ManageScoopModal from 'components/ManageScoopModal';
import AYLink from 'components/AYLink';
import { I18N_PLATFORM_COMMON_WORD_PATH } from 'constants/i18n';
import { subscriptionsContext } from 'contexts/SubscriptionsContext';
import { IUser } from 'interfaces/IUser';
import { IPropertySet } from 'interfaces/inputs/IPropertySet';
import { PropertyCompSetModalForm } from 'pages/PropertyPage/PropertyCompSetModalForm';
import locations from 'routes';
import { translateText } from 'utils/i18n';

import UserAvatar from '../UserAvatar';
import OptionsButton from '../OptionsButton';
import { SearchInputSize } from '../SearchInput/enums';
import { getAddButtonOptions } from './utils';
import styles from './Navbar.module.scss';

type PropTypes = {
  wrapperClassName?: string;
  isSearchVisible?: boolean;
  user: IUser;
};

export const NAVBAR_HEIGHT = 56;

const SCOOP_MODAL_KEY = 'scoop_modal';
const HISTORICAL_PERFORMANCE_MODAL_KEY = 'historical_performance_modal';
const COMP_SET_MODAL_KEY = 'comp_set_modal';

export const Navbar = ({
  wrapperClassName = '',
  isSearchVisible = false,
  ...props
}: PropTypes) => {
  const history = useHistory();
  const { hasEditorAccess } = useContext(subscriptionsContext);

  const showAiAbstraction =
    window._env_['REACT_APP_DISPLAY_AI_ABSTRACTION'] === 'true';

  const [modalsVisibility, setModalsVisibility] = useState({
    [SCOOP_MODAL_KEY]: false,
    [HISTORICAL_PERFORMANCE_MODAL_KEY]: false,
    [COMP_SET_MODAL_KEY]: false,
  });

  const toggleModal = (modal: any, isVisible: boolean) =>
    setModalsVisibility({
      ...modalsVisibility,
      [modal]: isVisible,
    });

  const handlePropertyCompSetSubmit = (propertySet: IPropertySet) => {
    if (propertySet.targetProperty?.id) {
      history.push(locations.showProperty(propertySet.targetProperty.id));
    }
  };

  return (
    <div
      className={classnames(styles.navbar, wrapperClassName, {
        [styles['navbar-border']]: isSearchVisible,
      })}
    >
      <div className={styles['navbar-header']} key="img">
        <Link to={locations.root()}>
          <img src={LogoImg} alt="Avison Young" />
        </Link>
        <NavBarGridControl />
        <hr className={styles['navbar-divider']} />
        <span className={styles['navbar-text']}>
          {translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.propertyIntelligence`,
          )}
        </span>
      </div>

      {isSearchVisible && (
        <div className={styles.search} key="search">
          <CompaniesPropertiesSearchInput
            autofocus={false}
            selectable
            navbar
            size={SearchInputSize.Small}
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.search`,
            )}
            withMarketSwitch
            includeSubmarkets
            includeMarkets
            includeMapBoxResults
          />
        </div>
      )}
      <div className={styles.settings}>
        <AYLink to="/projects" wrapperClassName={styles['settings-projects']}>
          {translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.projects`)}
        </AYLink>
        {!!props.user.id && (
          <>
            {hasEditorAccess && (
              <div>
                <OptionsButton
                  label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.add`)}
                  dropdownOptions={getAddButtonOptions(
                    history,
                    () => toggleModal(SCOOP_MODAL_KEY, true),
                    () => toggleModal(HISTORICAL_PERFORMANCE_MODAL_KEY, true),
                    () => toggleModal(COMP_SET_MODAL_KEY, true),
                    showAiAbstraction,
                  )}
                  optionsContainerStyle="contextual-positive"
                  optionsButtonClassName={styles['settings-button']}
                  optionClassName={styles['settings-option']}
                />
              </div>
            )}
            <div>
              <Link
                to={locations.userSettings()}
                className={styles['settings-profile']}
              >
                <UserAvatar user={props.user} />
              </Link>
            </div>
          </>
        )}

        <ManageScoopModal
          onCloseModal={() => toggleModal(SCOOP_MODAL_KEY, false)}
          isVisible={modalsVisibility[SCOOP_MODAL_KEY]}
        />

        {modalsVisibility[HISTORICAL_PERFORMANCE_MODAL_KEY] && (
          <CreateHistoricalPerformance
            onClose={() => toggleModal(HISTORICAL_PERFORMANCE_MODAL_KEY, false)}
          />
        )}

        {modalsVisibility[COMP_SET_MODAL_KEY] && (
          <PropertyCompSetModalForm
            onCancel={() => toggleModal(COMP_SET_MODAL_KEY, false)}
            onSubmit={handlePropertyCompSetSubmit}
          />
        )}
      </div>
    </div>
  );
};
